/**
 * The `TokenHandler` postMessage exchange with the host portal (MA-227).
 *
 * Separate from `messages.ts` on purpose: that file is OUR envelope
 * (`{ source: "tessa-embed" | "tessa-portal", type, version, payload }`), a
 * contract we defined and the portal implements. This is SYNIGO'S envelope —
 * their shape, their field names, their vendor — and we implement it. Mixing the
 * two would put a foreign schema behind `isInboundMessage` and make the embed
 * bridge responsible for authentication.
 *
 * The exchange, in full:
 *
 *   child  -> parent:  { action: "TokenHandler", tenantId, payload: { resource } }
 *   parent -> child:   { success: true,  result: "<access token>" }
 *                   |  { success: false, error:  "<reason>" }
 *
 * No expiry is returned, which is why the provider decodes `exp` from the JWT
 * itself (see `portalTokenProvider.ts`), and "refresh" is simply re-sending the
 * request — Synigo's token service hands back a fresh token when the one it
 * holds has under 5-10 minutes of life left.
 *
 * Type guards are hand-rolled (no zod / no new deps) and pure, matching
 * `messages.ts`.
 */

import { getEmbedConfig } from "@/lib/embed/config";

/** Synigo's action discriminator. Their literal, not ours — do not rename. */
export const PORTAL_TOKEN_ACTION = "TokenHandler" as const;

/**
 * How long to wait for a reply before giving up.
 *
 * 2500 ms is a budget, not a measurement: it is how long the user stares at the
 * existing "Aanmelden…" spinner before the gate gives up and starts today's
 * flow. There is no reply at all from a portal that is not listening — a
 * postMessage into a parent with no handler is silently discarded — so a timeout
 * is the only way this path can end when the flag is on and the host does not
 * implement the exchange.
 */
export const PORTAL_TOKEN_TIMEOUT_MS = 2500;

export interface PortalTokenRequest {
  action: typeof PORTAL_TOKEN_ACTION;
  tenantId: string;
  payload: { resource: string };
}

export type PortalTokenReply =
  | { success: true; result: string }
  | { success: false; error: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function buildTokenRequest(
  tenantId: string,
  resource: string,
): PortalTokenRequest {
  return {
    action: PORTAL_TOKEN_ACTION,
    tenantId,
    payload: { resource },
  };
}

/**
 * Validate that `data` is a well-formed `TokenHandler` reply.
 *
 * Matches on PAYLOAD SHAPE ONLY — never on ordering, never on "this is the only
 * message you will receive".
 *
 * Synigo described their reply as "the one and only message you will receive
 * from us", and trusting that would be shorter: take the first message that
 * arrives and read `result` off it. But it is a weak discriminator. Our own
 * portal protocol (`messages.ts`) already posts `theme` messages from this same
 * parent, browser extensions and embedded third-party scripts post into frames
 * unbidden, and React DevTools does too in development. Any of those arriving
 * first would be read as the token reply. Shape is the only thing we can check
 * for ourselves, so shape is what we check.
 *
 * Note there is no `source` or `version` field to key off — this envelope is
 * Synigo's and it has neither, and `action` is not echoed back either.
 */
export function isTokenReply(data: unknown): data is PortalTokenReply {
  if (!isRecord(data)) return false;
  if (typeof data.success !== "boolean") return false;
  return data.success
    ? typeof data.result === "string"
    : typeof data.error === "string";
}

/**
 * The exchange in flight, if any, so that concurrent callers coalesce onto one.
 *
 * Without it the gate's own probe and the first API call would each post a
 * request and each install a listener, and both would then resolve off whichever
 * reply landed first regardless.
 */
let inFlight: Promise<string> | undefined;

/**
 * Ask the host portal for an access token, once.
 *
 * Resolves with the raw access token, or rejects: on `success: false`, on a
 * missing configuration, or after {@link PORTAL_TOKEN_TIMEOUT_MS}. Callers get
 * failure as a rejection because this function makes no policy decisions —
 * caching, fallback and the "stop asking" latch all live in
 * `portalTokenProvider.ts`.
 *
 * Three checks on every inbound message, the same three the embed bridge
 * applies: `event.origin` is the configured portal origin, `event.source` is
 * `window.parent`, and the payload passes {@link isTokenReply}. A message that
 * fails any of them is IGNORED rather than treated as a failure — the listener
 * keeps waiting until the timeout, because an unrelated message arriving first
 * must not consume our one attempt.
 */
export function requestPortalToken(): Promise<string> {
  if (inFlight) return inFlight;

  const { allowedParentOrigin, tenantId, portalTokenResource } =
    getEmbedConfig();

  // Fail closed. Each of these is needed to post the request at all, and an
  // absent one is a misconfiguration on our side rather than a portal that said
  // no. Not cached and not latched: there is nothing to retry and nothing to
  // give up on.
  if (!allowedParentOrigin || !tenantId || !portalTokenResource) {
    return Promise.reject(new Error("portal token exchange is not configured"));
  }

  // Cleared however it settles, so a later call (a refresh, or a retry after the
  // popup path) starts a fresh exchange rather than replaying this one's result.
  // The clearing lives in `exchange`'s own `finally` -- see the note there for
  // why it cannot be a `.finally()` chained on this promise.
  inFlight = exchange(allowedParentOrigin, tenantId, portalTokenResource);
  return inFlight;
}

/**
 * One request/reply round trip: post, wait, tear down.
 *
 * Separate from {@link requestPortalToken} only so the listener, the timer and
 * the `inFlight` handle can be released in a `finally` that covers all three ways
 * this settles — reply, refusal, timeout — instead of being repeated at each of
 * them.
 */
async function exchange(
  allowedParentOrigin: string,
  tenantId: string,
  resource: string,
): Promise<string> {
  let handleMessage: ((event: MessageEvent) => void) | undefined;
  let timer: number | undefined;

  try {
    return await new Promise<string>((resolve, reject) => {
      let settled = false;

      handleMessage = (event: MessageEvent) => {
        if (!isTokenReply(event.data)) return;

        // A reply that looks right but comes from the wrong place. This is the
        // one console line in this module worth having: it is the single prd
        // failure we could otherwise not tell apart from "the portal never
        // answered", and it is the plausible one — if the portal relays its reply
        // through a nested frame, a worker or an opener rather than posting from
        // the window that frames us, `event.source === window.parent` fails while
        // everything else about the message looks perfect.
        //
        // The check stays fail-closed. These two conditions are half of the
        // whole defence and neither half substitutes for the other: nginx's
        // `Content-Security-Policy: frame-ancestors <portal-origin>` decides who
        // may FRAME us, and `event.origin` + `event.source === window.parent`
        // decide who may HAND US A TOKEN. Accepting an unverified sender's token
        // would mean sending a stranger's bearer token to our own backend under
        // the user's session. We only say out loud that we rejected one.
        if (
          event.origin !== allowedParentOrigin ||
          event.source !== window.parent
        ) {
          console.warn(
            "[portal-token] discarded a token reply from an unexpected sender",
            {
              origin: event.origin,
              expectedOrigin: allowedParentOrigin,
              fromParent: event.source === window.parent,
            },
          );
          return;
        }

        // Late and duplicate replies are dropped. The `finally` below has already
        // removed this listener by then, but a message already dispatched into it
        // must not resolve a settled promise or log a second time.
        if (settled) return;
        settled = true;

        if (event.data.success) {
          resolve(event.data.result);
          return;
        }

        // `success` and a truncated reason only. Never the token, and never the
        // raw reply — on the other branch that object *is* the token.
        console.warn("[portal-token] portal refused the token request", {
          success: false,
          error: event.data.error.slice(0, 200),
        });
        reject(new Error("portal refused the token request"));
      };

      timer = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error("portal token request timed out"));
      }, PORTAL_TOKEN_TIMEOUT_MS);

      window.addEventListener("message", handleMessage);

      // Explicit `targetOrigin`, always — never "*". Same rule as the embed
      // bridge, and it matters more here: "*" would broadcast a request naming
      // our tenant and API resource to whatever happens to be framing us.
      //
      // Synigo's sample used `window.parent.location.origin` for this. That
      // THROWS on a cross-origin parent: per the same-origin policy, `origin` is
      // not a readable property of a cross-origin `Location` — the only
      // cross-origin members are `location.replace()` and the write-only
      // `location.href`. The portal is by definition a different origin from the
      // embed, so that line could never have run. We post to the origin we were
      // configured with instead, and validate the reply against the same value.
      // Do not "fix" this back.
      //
      // (Their sample also had a missing comma after `tenantId` and a stray `)`
      // closing the `postMessage` call early, so it was pseudocode rather than
      // something that had been run. The shape is right; the code was not.)
      window.parent.postMessage(
        buildTokenRequest(tenantId, resource),
        allowedParentOrigin,
      );
    });
  } finally {
    if (handleMessage) window.removeEventListener("message", handleMessage);
    if (timer !== undefined) window.clearTimeout(timer);

    // Released HERE, inside the async function body, and not from a `.finally()`
    // chained onto this promise by the caller. The `finally` block of an async
    // function runs BEFORE that function's promise settles, so "`inFlight` is
    // set" genuinely implies "the exchange has not settled yet" -- which is what
    // the comment above `inFlight` claims and what makes the coalescing sound.
    //
    // A `.finally()` on the caller side runs one microtask AFTER settlement, and
    // in that gap `inFlight` is an already-settled promise: a caller arriving
    // there gets a replayed result instead of a new exchange. For a token that is
    // barely a tick from being handed out that is nearly harmless; for a
    // rejection it silently re-serves a failure. It also gave the two callers of
    // `requestPortalToken` different cleanup timing depending on which branch
    // returned the promise. One place, one moment, both callers.
    inFlight = undefined;
  }
}
